import React, { useState } from 'react';
import { TextField, Button, Box, Alert, InputAdornment, IconButton, useTheme, Zoom } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { login } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';
import { useLang } from '../../contexts/langContext';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';

interface FieldErrors {
  email?: string;
  password?: string;
}

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { lang } = useLang();
  const { setUser } = useAuth();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setIsSubmitting(true);

    // Basic client-side validation
    const newFieldErrors: FieldErrors = {};
    if (!email) newFieldErrors.email = lang === 'en' ? 'Email is required' : '邮箱是必填项';
    if (!password) newFieldErrors.password = lang === 'en' ? 'Password is required' : '密码是必填项';

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const user = await login(email, password);
      setUser(user);
      navigate('/projects');
    } catch (error: any) {
      if (error.response && error.response.data) {
        if (error.response.status === 410) {
          setFormError(
            lang === 'en'
              ? error.response.data.message || 'This login method has been retired. Please use a local password or OAuth to sign in.'
              : '该登录方式已停用，请使用本地密码或 OAuth 登录。'
          );
          return;
        }
        if (error.response.data.errorCode) {
          // Handle field-specific errors
          switch (error.response.data.errorCode) {
            case 'user_not_found':
              setFieldErrors({ email: lang === 'en' ? 'User not found' : '用户不存在' });
              break;
            case 'incorrect_password':
              const authType = error.response.data.authType || '';
              const authTypeText = authType === 'seeyon_chat' ? (lang === 'en' ? '(Seeyon Chat)' : '(致慧泉)') : authType === 'local' ? '' : authType;
              setFieldErrors({ password: lang === 'en' ? `Incorrect password ${authTypeText}` : `密码错误 ${authTypeText}` });
              break;
            case 'invalid_credentials':
              setFormError(lang === 'en' ? 'Invalid credentials' : '无效的凭证');
              break;
            default:
              setFormError(error.response.data.message || (lang === 'en' ? 'An error occurred during login' : '登录时发生错误'));
          }
        } else {
          setFormError(
            error.response.data.message || error.response.data.error || (lang === 'en' ? 'An error occurred during login' : '登录时发生错误')
          );
        }
      } else {
        setFormError(lang === 'en' ? 'An unexpected error occurred' : '发生了一个未知的错误');
        console.error('An unexpected error occurred:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      noValidate 
    >
      {formError && (
        <Zoom in>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            {formError}
          </Alert>
        </Zoom>
      )}
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label={lang === 'en' ? 'Email Address' : '邮箱地址'}
        name="email"
        autoComplete="email"
        autoFocus
        size="small"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!fieldErrors.email}
        helperText={fieldErrors.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email sx={{ color: theme => theme.palette.primary.main, fontSize: '1.2rem' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
          },
          '& .MuiInputLabel-root': {
            color: theme => theme.palette.text.secondary,
            fontSize: '0.875rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
          },
        }}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label={lang === 'en' ? 'Password' : '密码'}
        type={showPassword ? 'text' : 'password'}
        id="password"
        size="small"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!fieldErrors.password}
        helperText={fieldErrors.password}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock sx={{ color: theme => theme.palette.primary.main, fontSize: '1.2rem' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                edge="end"
                size="small"
                sx={{ 
                  color: theme => theme.palette.primary.main,
                }}
              >
                {showPassword ? <VisibilityOff sx={{ fontSize: '1.2rem' }} /> : <Visibility sx={{ fontSize: '1.2rem' }} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
          },
          '& .MuiInputLabel-root': {
            color: theme => theme.palette.text.secondary,
            fontSize: '0.875rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
          },
        }}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isSubmitting}
        sx={{
          mt: 1,
          py: 1,
          borderRadius: 2,
          fontSize: '0.9rem',
          fontWeight: 600,
          textTransform: 'none',
          background: theme => theme.palette.primary.main,
          '&.Mui-disabled': {
            background: theme => theme.palette.action.disabledBackground,
          },
        }}
      >
        {isSubmitting 
          ? (lang === 'en' ? 'Signing in...' : '登录中...') 
          : (lang === 'en' ? 'Sign In' : '登录')
        }
      </Button>
    </Box>
  );
};

export default LoginForm;